import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import useDialog from '@shared/hooks/src/useDialog'
import { Indicator } from '@shared/hooks/src/useIndicator'
import { RoleGuard, useMe } from '@shared/providers/src/MeProvider'
import { queryClient } from '@shared/providers/src/QueryClientProvider'
import { handleError, LabStatus, QK, UserRole } from '@shared/utils'

import { LabDetailsModal } from '@pages/Labs/Lab'
import { useLabReviewCheck, useLabUpdate } from '@pages/Labs/Lab/Lab.hooks'
import { isLabFailed } from '@pages/Labs/Lab/Lab.utils'
import { styles } from '@pages/Labs/Labs.utils'
import ConfirmationV2 from '@components/Dialog/ConfirmationV2'
import StatusChip from '@components/StatusChip'
import { Item } from '@components/TableCell'
import Provider from '@components/TableCell/Provider'

export default function LabsForReview({ provider, labs, open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      sx={{ '& .MuiDialog-container': { justifyContent: 'flex-end' } }}
    >
      <Content provider={provider} labs={labs} onClose={onClose} />
    </Dialog>
  )
}

function Content({ provider, labs = [], onClose }) {
  const [open, setOpen] = useState(false)
  const [detailedLab, setDetailedLab] = useState(false)

  const me = useMe()
  const updateLab = useLabUpdate()

  const sortedLabs = useMemo(() => {
    return [...labs].sort((a, b) => {
      if (a.hivPositive !== b.hivPositive) {
        return b.hivPositive - a.hivPositive
      }

      if (a.positiveResult !== b.positiveResult) {
        return b.positiveResult - a.positiveResult
      }

      return new Date(a.updatedAt) - new Date(b.updatedAt)
    })
  }, [labs])

  const reviewLab = useDialog({
    component: ConfirmationV2,
    props: ({ item, close }) => ({
      title: 'Review Lab?',
      description: 'Are you sure you want to review this lab?',
      onReject: () => close(),
      onConfirm: () =>
        updateLab.mutateAsync({ labId: item, status: LabStatus.Reviewing }).then(() => {
          close()
          queryClient.invalidateQueries({ queryKey: QK.users.id(me.id).indicator(Indicator.NeedReviewLabs).lists })
          window.open(`/app/labs/${item}`, '_blank')
        }),
    }),
  })

  return (
    <>
      <LabDetailsModal align="flex-start" labId={detailedLab} open={open} onClose={() => setOpen(false)} />
      <DialogTitle onClose={onClose}>
        <Provider user={provider} />
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell width={70}>State</TableCell>
                <TableCell>Encounter</TableCell>
                <TableCell>Panel</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <RoleGuard allowed={[UserRole.Provider]}>
                  <TableCell />
                </RoleGuard>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLabs.map((lab) => (
                <LabRow
                  key={lab.id}
                  data={lab}
                  onClick={() => {
                    setDetailedLab(lab.id)
                    setOpen(true)
                  }}
                  onReview={() => reviewLab(lab.id)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </>
  )
}

function LabRow({ data: lab, onClick, onReview }) {
  const isFailed = isLabFailed(lab.status)
  const isPositive = lab.positiveResult
  const isHIVPositive = lab.hivPositive

  return (
    <TableRow hover onClick={onClick} sx={[styles.positive(isPositive, isHIVPositive), { cursor: 'pointer' }]}>
      <TableCell>
        <Typography variant="body2">{lab.id}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{lab.patientHomeState || '—'}</Typography>
      </TableCell>
      <TableCell>
        <Item small primary={lab.encounterReason} secondary={lab.lab} />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{lab.panelsToString}</Typography>
      </TableCell>
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="body2" sx={{ color: isFailed ? 'error.main' : 'text.primary', fontWeight: isFailed ? 'bold' : 'normal' }}>
          {lab.status}
        </Typography>
        {isPositive && <StatusChip.Lab positive hiv={isHIVPositive} />}
      </TableCell>
      <TableCell>
        <Typography variant="body2">{dayjs(lab.updatedAt).format('L')}</Typography>
      </TableCell>
      <RoleGuard allowed={[UserRole.Provider]}>
        <TableCell align="right">
          <ReviewButton labId={lab.id} onReview={onReview} />
        </TableCell>
      </RoleGuard>
    </TableRow>
  )
}

function ReviewButton({ labId, onReview }) {
  const reviewCheck = useLabReviewCheck(labId)

  const handleClick = (e) => {
    e.stopPropagation()
    reviewCheck
      .mutateAsync()
      .then(({ can_review }) => {
        if (can_review) return onReview()
        toast.error('You are not assigned to the service line of this lab')
      })
      .catch((e) => handleError(e, { showResponse: true }))
  }

  return (
    <Button size="small" variant="outlined" loading={reviewCheck.isPending} onClick={handleClick}>
      Review Lab
    </Button>
  )
}
